import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Row as AntRow,
  Col,
  Typography,
  DatePicker,
  Checkbox,
  InputNumber,
  Divider,
  Row,
  Tooltip,
} from "antd";
import IncredableButton from "../../../components/button";
import { Attribute } from "../../../redux/api/section/types";
import { TypeOptions } from "../add-section";
import { Countries, USStatesList } from "../../../common/constants";
import EditableOptionTable, { OptionItem } from "./editable-option-table";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Text, Title } = Typography;
const SectionTitle = ({ title }: { title: string }) => (
  <Title level={5} style={{ marginTop: 24 }}>
    {title}
  </Title>
);

interface AddAttributeModalProps {
  isFieldsModalOpen: boolean;
  setIsFieldsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleAddAttribute: () => void;
  sectionAttribute: Attribute;
  setSectionAttribute: React.Dispatch<React.SetStateAction<Partial<Attribute>>>;
  attributeType: string;
}
const AddAttributeModal: React.FC<AddAttributeModalProps> = ({
  isFieldsModalOpen,
  setIsFieldsModalOpen,
  handleAddAttribute,
  sectionAttribute,
  setSectionAttribute,
  attributeType,
}) => {
  const [selectedStateValues, setSelectedStateValues] = useState<string[]>([]);
  const [selectedCountryValues, setSelectedCountryValues] = useState<string[]>(
    []
  );
  const [form] = Form.useForm();

  useEffect(() => {
    if (!isFieldsModalOpen) return;

    if (attributeType === "States") {
      const defaultOptions = USStatesList.map((state, index) => ({
        id: `state-${index}`,
        label: state.value,
        value: state.value,
        selectedValue: [state.value],
      }));
      const allValues = USStatesList.map((state) => state.value);
      setSelectedStateValues(allValues);
      setSectionAttribute((prev) => ({
        ...prev,
        optionDefinition: defaultOptions,
      }));
    } else if (attributeType === "Countries") {
      const defaultOptions = Countries.map((country, index) => ({
        id: `country-${index}`,
        label: country.value,
        value: country.value,
        selectedValue: [country.value],
      }));
      const allValues = Countries.map((c) => c.value);
      setSelectedCountryValues(allValues);
      setSectionAttribute((prev) => ({
        ...prev,
        optionDefinition: defaultOptions,
      }));
    } else {
      setSelectedCountryValues([]);
      setSelectedStateValues([]);
      setSectionAttribute((prev) => ({
        ...prev,
        optionDefinition: [],
      }));
    }
  }, [isFieldsModalOpen, attributeType]);

  const handleStateChange = useCallback((values: string[]) => {
    setSelectedStateValues(values);
    updateOptionDefinition(values);
  }, []);

  const handleCountryChange = useCallback((values: string[]) => {
    setSelectedCountryValues(values);
    updateOptionDefinition(values);
  }, []);

  const updateOptionDefinition = (values: string[]) => {
    setSectionAttribute((prev) => {
      if (!prev) return prev;
      const existingMap = new Map(
        (prev.optionDefinition || []).map((opt) => [opt.value, opt])
      );
      const updated = values.map(
        (val, i) => existingMap.get(val) || { label: val, value: val }
      );
      return { ...prev, optionDefinition: updated };
    });
  };

  const renderTypeSpecificFields = () => {
    if (
      attributeType === "Dropdown.Single" ||
      attributeType === "Dropdown.Multiple"
    ) {
      return (
        <>
          <SectionTitle title="Field Options" />
          <EditableOptionTable
            options={
              (sectionAttribute.optionDefinition as OptionItem[]) || []
            }
            setOptions={(opts) =>
              setSectionAttribute({
                ...sectionAttribute,
                optionDefinition: opts,
              })
            }
          />
        </>
      );
    }
    if (attributeType === "States") {
      return (
        <Select
          mode="multiple"
          allowClear
          value={selectedStateValues}
          style={{ width: "100%" }}
          placeholder="Select states"
          onChange={handleStateChange}
          options={USStatesList.map((opt) => ({
            label: opt.value,
            value: opt.value,
          }))}
        />
      );
    }
    if (attributeType === "Countries") {
      return (
        <Select
          mode="multiple"
          allowClear
          value={selectedCountryValues}
          style={{ width: "100%" }}
          placeholder="Select countries"
          onChange={handleCountryChange}
          options={Countries.map((opt) => ({
            label: opt.value,
            value: opt.value,
          }))}
        />
      );
    }
    return null;
  };

  return (
    <Modal
      title={<Title level={4}>Add Field</Title>}
      open={isFieldsModalOpen}
      onCancel={() => {
        form.resetFields();
        setSectionAttribute({});
        setSelectedStateValues([]);
        setSelectedCountryValues([]);
        setIsFieldsModalOpen(false);
      }}
      footer={null}
      width="80vw"
      style={{ top: 50 }}
      styles={{
        body: { maxHeight: "75vh", overflowY: "auto" }
      }}
    >
      <Form layout="vertical" form={form}>
        <Card style={{
          boxShadow: "none",
          border: "none",
        }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="Field Type">
                <Select
                  value={attributeType}
                  disabled={attributeType ? true : false}
                  options={TypeOptions}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label={
                  <span style={{ fontWeight: "400" }}>
                    Field Name <Text type="danger">*</Text>
                  </span>
                }
                rules={[{ required: true, message: "Field Name is required" }]}
              >
                <Input
                  value={sectionAttribute.name}
                  onChange={(e) =>
                    setSectionAttribute({
                      ...sectionAttribute,
                      name: e.target.value,
                    })
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          {attributeType !== "Divider" && (
            <Form.Item
              label={"Hint Text"}
            >
              <Input
                value={sectionAttribute.description}
                onChange={(e) =>
                  setSectionAttribute({
                    ...sectionAttribute,
                    description: e.target.value,
                  })
                }
              />
            </Form.Item>
          )}

          <SectionTitle title="Behavior Settings" />
          <Row gutter={16}>
            <Col span={12}>
              <Checkbox
                checked={sectionAttribute.required}
                onChange={(value) =>
                  setSectionAttribute({
                    ...sectionAttribute,
                    required: value?.target?.checked,
                  })
                }
              >
                Required Field
              </Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox
                checked={sectionAttribute.sensitive}
                onChange={(value) =>
                  setSectionAttribute({
                    ...sectionAttribute,
                    sensitive: value?.target?.checked,
                  })
                }
              >
                Sensitive Info{" "}
                <Tooltip title="E.g., SSN or ID. Access limited by role.">
                  <InfoCircleOutlined />
                </Tooltip>
              </Checkbox>
            </Col>
          </Row>

          {attributeType !== "Divider" && (
            <>{renderTypeSpecificFields()}</>
          )}

          <SectionTitle title="Preview" />
          <Card style={{
            marginTop: 12,
            border: "1px solid #f0f0f0",
            borderRadius: 8,
          }}>
            <Form.Item
              label={
                <>
                  {attributeType === "Divider" ? (
                    <></>
                  ) : (
                    <span style={{ fontWeight: "600" }}>
                      {sectionAttribute.name}{" "}
                      {!!sectionAttribute.required ? (
                        <Text type="danger">*</Text>
                      ) : (
                        <></>
                      )}
                    </span>
                  )}
                </>
              }
            >
              {typeFields(attributeType, sectionAttribute)}
            </Form.Item>
          </Card>

          <Divider />

          <Row justify="end">
            <Space>
              <Button onClick={() => {
                form.resetFields();
                setSectionAttribute({});
                setSelectedStateValues([]);
                setSelectedCountryValues([]);
                setIsFieldsModalOpen(false);
              }}>
                Cancel
              </Button>
              <IncredableButton type="primary" onClick={handleAddAttribute}>
                Save Changes
              </IncredableButton>
            </Space>
          </Row>
        </Card>
      </Form>
    </Modal>
  );
};

export default AddAttributeModal;

export const typeFields = (
  attributeType: string,
  sectionAttribute: Attribute
) => {
  switch (attributeType || sectionAttribute?.type) {
    case "Dropdown.Single":
      return (
        <Select
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
          options={sectionAttribute?.optionDefinition?.map((option) => ({
            value: option?.value,
            label: option?.label,
          }))}
        />
      );
    case "States":
      return (
        <Select
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
          // value={sectionAttribute.optionDefinition?.map((opt) => opt.value)}
          options={sectionAttribute?.optionDefinition?.map((option) => ({
            value: option?.value,
            label: option?.label,
          }))}
        />
      );
    case "Countries":
      return (
        <Select
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
          options={sectionAttribute?.optionDefinition?.map((option) => ({
            value: option?.value,
            label: option?.label,
          }))}
        />
      );

    case "Date":
      return (
        <DatePicker
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
        />
      );

    case "Checkbox":
      return <Checkbox>{sectionAttribute.description}</Checkbox>;

    case "Number":
      return (
        <InputNumber<number>
          style={{ width: "100%" }}
          defaultValue={1000}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) =>
            value?.replace(/\$\s?|(,*)/g, "") as unknown as number
          }
          placeholder={sectionAttribute.description}
        />
      );
    case "Textarea":
      return (
        <Input.TextArea
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
        />
      );

    case "Divider":
      return (
        <Divider orientation="left" style={{ width: "100%" }}>
          {sectionAttribute.name}
        </Divider>
      );
    default:
      return (
        <Input
          style={{ width: "100%" }}
          placeholder={sectionAttribute.description}
        />
      );
  }
};
